import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../components/ConfirmModal';
import DashboardPagination from '../components/DashboardPagination';
import PreProjetoFiltros from '../components/PreProjetoFiltros';
import { filtrosVazios, passaNosFiltros, type FiltrosPreProjeto } from '../utils/preProjetoFiltros';
import { useAuth } from '../context/AuthContext';
import { Permission } from '../types/user.types';
import preProjetoService, { type PreProjetoDTO } from '../services/preProjetoService';
import parametroSistemaService from '../services/parametroSistemaService';
import { UI_THEME } from '../ui/theme';
import Button from '../components/ui/Button';
import { FIELD_BASE_CLASS, FIELD_CLASS } from '../components/ui/fieldStyles';

const money = (value: number) => new Intl.NumberFormat('pt-BR', {
  style: 'currency', currency: 'BRL',
}).format(value || 0);

// Paleta categórica do gráfico: as fatias precisam se distinguir entre si nos dois modos, então não vira token.
const DONUT_COLORS = ['#0d9488', '#6366f1', '#f59e0b', '#ef4444', '#0ea5e9', '#84cc16', '#a855f7', '#f97316']; // ui-audit:hex-ok

type AreaSlice = { area: string; qtd: number; pct: number; color: string };
type DonutSegment = AreaSlice & { dasharray: string; dashoffset: number };

const groupByArea = (list: PreProjetoDTO[]): AreaSlice[] => {
  const grupos = new Map<string, number>();
  list.forEach((item) => {
    const area = item.areaAtuacao || 'Sem área definida';
    grupos.set(area, (grupos.get(area) || 0) + 1);
  });
  const total = list.length || 1;
  return Array.from(grupos.entries())
    .map(([area, qtd], i) => ({ area, qtd, pct: (qtd / total) * 100, color: DONUT_COLORS[i % DONUT_COLORS.length] }))
    .sort((a, b) => b.qtd - a.qtd);
};

const buildSegments = (slices: AreaSlice[]): DonutSegment[] => {
  const circumference = 2 * Math.PI * 40;
  let offset = 0;
  return slices.map((slice) => {
    const length = (slice.pct / 100) * circumference;
    const segment = { ...slice, dasharray: `${length} ${circumference - length}`, dashoffset: -offset };
    offset += length;
    return segment;
  });
};

const DonutCard: React.FC<{
  title: string;
  subtitle?: string;
  slices: AreaSlice[];
  segments: DonutSegment[];
  emptyText: string;
  footer?: React.ReactNode;
  headerExtra?: React.ReactNode;
}> = ({ title, subtitle, slices, segments, emptyText, footer, headerExtra }) => (
  <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
    <div className="flex items-center justify-between gap-2">
      <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
      {subtitle && <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{subtitle}</span>}
    </div>
    {headerExtra}
    {slices.length === 0 ? (
      <p className="mt-6 text-sm text-slate-500 dark:text-slate-400">{emptyText}</p>
    ) : (
      <>
        <div className="mt-4 flex justify-center">
          <svg viewBox="0 0 100 100" className="h-40 w-40 -rotate-90">
            <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="14" className="text-slate-100 dark:text-slate-800" />
            {segments.map((s) => (
              <circle key={s.area} cx="50" cy="50" r="40" fill="none" stroke={s.color} strokeWidth="14"
                strokeDasharray={s.dasharray} strokeDashoffset={s.dashoffset} strokeLinecap="butt" />
            ))}
          </svg>
        </div>
        <ul className="mt-4 space-y-2">
          {slices.map((slice) => (
            <li key={slice.area} className="flex items-center justify-between gap-2 text-xs">
              <span className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: slice.color }} />
                {slice.area}
              </span>
              <span className="font-semibold text-slate-900 dark:text-slate-100">{slice.pct.toFixed(0)}%</span>
            </li>
          ))}
        </ul>
      </>
    )}
    {footer}
  </div>
);

const GestaoPreProjetosPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const isSaas = UI_THEME === 'saas';
  const canSelect = hasPermission(Permission.SELECT_PRE_PROJETO);

  const [items, setItems] = useState<PreProjetoDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [target, setTarget] = useState<PreProjetoDTO | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<PreProjetoDTO | null>(null);
  const [cancelJustificativa, setCancelJustificativa] = useState('');
  const [cancelando, setCancelando] = useState(false);
  const [cancelamentoAberto, setCancelamentoAberto] = useState(false);
  const [somenteBloqueadas, setSomenteBloqueadas] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtros, setFiltros] = useState<FiltrosPreProjeto>(filtrosVazios);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const load = (somenteBloqueadas: boolean) => {
    setLoading(true);
    // Catálogo da seleção: igual pra todo mundo, independente do papel. Por padrão
    // não traz instituição bloqueada (ela não pode ser escolhida mesmo). Marcando o
    // filtro, pedimos a lista completa pro backend (que sabe quem está bloqueada) e
    // aqui só restringimos ao que veio marcado -- não reimplementamos a regra.
    preProjetoService.catalogo(somenteBloqueadas)
      .then((data) => setItems(somenteBloqueadas ? data.filter((i) => i.instituicaoBloqueada) : data))
      .catch((e) => setError(e?.response?.data?.error || 'Não foi possível carregar os pré-projetos.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(somenteBloqueadas); }, [somenteBloqueadas]);
  // O período vem do backend; ele continua sendo quem decide ao cancelar.
  useEffect(() => {
    parametroSistemaService.getParametros()
      .then((p) => setCancelamentoAberto(!!p.preProjetoCancelamentoAberto))
      .catch(() => setCancelamentoAberto(false));
  }, []);
  useEffect(() => { setPage(1); }, [somenteBloqueadas, filtros, busca]);

  // Os filtros valem para a lista de cards; os gráficos abaixo continuam sobre o catálogo inteiro.
  const filteredItems = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return items.filter((item) => passaNosFiltros(item, filtros)
      && (!termo || [item.codigo, item.titulo, item.objeto, item.instituicaoNome, item.areaAtuacao]
        .some((valor) => valor?.toLowerCase().includes(termo))));
  }, [items, filtros, busca]);

  const visibleItems = useMemo(
    () => filteredItems.slice((page - 1) * pageSize, page * pageSize),
    [filteredItems, page, pageSize],
  );

  const porArea = useMemo(() => groupByArea(items), [items]);
  const donutSegments = useMemo(() => buildSegments(porArea), [porArea]);

  const minhasSelecoes = useMemo(
    () => items.filter((i) => i.etapa === 'SELECIONADO' && i.selecionadoPorUsuarioId === user?.id),
    [items, user],
  );
  const minhaPorArea = useMemo(() => groupByArea(minhasSelecoes), [minhasSelecoes]);
  const minhaSegments = useMemo(() => buildSegments(minhaPorArea), [minhaPorArea]);
  const minhaExercicios = useMemo(() => {
    const anos = Array.from(new Set(minhasSelecoes.map((i) => i.exercicio))).sort((a, b) => a - b);
    if (anos.length === 0) return undefined;
    if (anos.length === 1) return `Exercício ${anos[0]}`;
    return `Exercícios ${anos[0]}–${anos[anos.length - 1]}`;
  }, [minhasSelecoes]);

  const handleConfirmSelect = async () => {
    if (!target) return;
    setConfirming(true);
    setError('');
    try {
      await preProjetoService.select(target.id);
      setSuccess(`"${target.titulo}" foi convertido em emenda.`);
      setTarget(null);
      load(somenteBloqueadas);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Não foi possível converter o pré-projeto em emenda.');
      setTarget(null);
    } finally {
      setConfirming(false);
    }
  };

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelando(true);
    setError('');
    try {
      await preProjetoService.cancelarSelecao(cancelTarget.id, cancelJustificativa.trim());
      setSuccess(`A seleção de "${cancelTarget.titulo}" foi cancelada e o projeto voltou ao catálogo.`);
      setCancelTarget(null);
      setCancelJustificativa('');
      load(somenteBloqueadas);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Não foi possível cancelar a seleção.');
      setCancelTarget(null);
    } finally {
      setCancelando(false);
    }
  };

  return (
    <div className="w-full space-y-6">
      <section className={`rounded-2xl border p-6 shadow-sm ${
        isSaas
          ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/50 to-indigo-50 dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
          : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-sky-50 dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
      }`}>
        <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-sky-700 dark:text-sky-400'}`}>
          Seleção de Projetos
        </p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-950 dark:text-slate-100">Escolha o seu projeto</h1>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
          Navegue pelas propostas publicadas pelas instituições e escolha a que você quer destinar como emenda.
          Ao escolher, o pré-projeto vira automaticamente uma emenda vinculada a você.
        </p>
      </section>

      {success && (
        <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 shadow-sm dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300">
          {success}
        </section>
      )}
      {error && (
        <section className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 shadow-sm dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </section>
      )}

      {loading ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-12 text-center text-sm text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-400">
          Carregando pré-projetos...
        </section>
      ) : (
        <>
          <section className="grid gap-6 md:grid-cols-2">
            <DonutCard
              title="Pré-projetos por área de atuação"
              slices={porArea}
              segments={donutSegments}
              emptyText="Nenhum pré-projeto no catálogo ainda."
            />
            <DonutCard
              title="Suas emendas por área de atuação"
              subtitle={minhaExercicios}
              slices={minhaPorArea}
              segments={minhaSegments}
              emptyText="Você ainda não converteu nenhum pré-projeto em emenda."
              footer={
                <div className="mt-4 border-t border-slate-100 pt-4 dark:border-slate-800">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Já convertidos em emenda</p>
                  <p className="mt-1 text-2xl font-bold tabular-nums text-teal-700 dark:text-teal-300">{minhasSelecoes.length}</p>
                </div>
              }
            />
          </section>

          <section className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800/70 lg:flex-row lg:flex-wrap lg:items-center">
            <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por título, objeto, instituição ou área"
              className={`${FIELD_BASE_CLASS} min-w-0 flex-1 lg:min-w-[22rem]`} />
            <PreProjetoFiltros items={items} value={filtros} onChange={setFiltros} comInstituicao comSelecao />
          </section>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <label className="flex w-fit items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <input
                type="checkbox"
                checked={somenteBloqueadas}
                onChange={(e) => setSomenteBloqueadas(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
              />
              Mostrar somente pré-projetos de instituições bloqueadas
            </label>
            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
              {filteredItems.length === items.length
                ? `${items.length} pré-projeto${items.length === 1 ? '' : 's'}`
                : `${filteredItems.length} de ${items.length} pré-projetos`}
            </span>
          </div>

          {filteredItems.length === 0 && (
            <section className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-400">
              {items.length > 0
                ? 'Nenhum pré-projeto encontrado com os filtros escolhidos.'
                : somenteBloqueadas
                  ? 'Nenhum pré-projeto de instituição bloqueada no momento.'
                  : 'Nenhum pré-projeto disponível no catálogo.'}
            </section>
          )}

          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {visibleItems.map((item) => {
              const taken = item.etapa === 'SELECIONADO';
              return (
                <article key={item.id} className={`flex flex-col rounded-2xl border p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900 ${
                  taken ? 'border-slate-200 bg-slate-50 dark:bg-slate-900/60' : 'border-slate-200 bg-white'
                }`}>
                  <div className="flex items-start justify-between gap-3">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                      item.categoriaFuncao === 'SAUDE'
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                        : 'bg-teal-50 text-teal-700 dark:bg-teal-950 dark:text-teal-300'
                    }`}>
                      {item.areaAtuacao || 'Sem área'}
                    </span>
                    {taken && (
                      <span className="rounded-full bg-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                        {item.selecionadoPor ? `Escolhido por ${item.selecionadoPor}` : 'Já escolhido'}
                      </span>
                    )}
                    {item.instituicaoBloqueada && (
                      <span className="rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700 dark:bg-red-950 dark:text-red-300">
                        Instituição bloqueada
                      </span>
                    )}
                  </div>
                  <p className="mt-4 font-mono text-xs font-semibold text-slate-500">{item.codigo}</p>
                  <h2 className="mt-1 text-base font-semibold text-slate-900 dark:text-white">{item.titulo}</h2>
                  <p className="mt-1 text-xs text-slate-500">{item.instituicaoNome}</p>
                  <p className="mt-1 inline-flex rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                    Exercício {item.exercicio}
                  </p>
                  <p className="mt-3 line-clamp-3 text-sm text-slate-600 dark:text-slate-300">{item.objeto}</p>
                  <div className="mt-5 border-t border-slate-100 pt-4 text-sm dark:border-slate-800">
                    <p className="text-xs text-slate-500">Valor solicitado</p>
                    <p className="font-semibold">{money(item.valorSolicitado)}</p>
                  </div>
                  <div className="mt-4 flex items-center gap-3">
                    <button onClick={() => navigate(`/dashboard/pre-projetos/${item.id}`)} className="text-sm font-semibold text-teal-700 hover:text-teal-800 dark:text-teal-300">
                      Ver detalhes →
                    </button>
                    {canSelect && !taken && !item.instituicaoBloqueada && (
                      <Button variant="primary" className="ml-auto" onClick={() => setTarget(item)}>
                        Escolher este projeto
                      </Button>
                    )}
                    {canSelect && taken && cancelamentoAberto
                      && (item.selecionadoPorUsuarioId === user?.id || user?.role === 'ADMIN') && (
                      <Button variant="danger-outline" size="sm" className="ml-auto" onClick={() => { setCancelJustificativa(''); setCancelTarget(item); }}>
                        Cancelar seleção
                      </Button>
                    )}
                  </div>
                </article>
              );
            })}
          </section>

          {filteredItems.length > 0 && (
            <DashboardPagination page={page} pageSize={pageSize} totalItems={filteredItems.length}
              onPageChange={setPage} onPageSizeChange={(value) => { setPageSize(value); setPage(1); }} itemLabel="pré-projetos" />
          )}
        </>
      )}

      <ConfirmModal
        open={!!target}
        title="Converter em emenda"
        body={target ? `Ao confirmar, "${target.titulo}" (${money(target.valorSolicitado)}) vira uma emenda vinculada a você e sai do catálogo. Essa ação não pode ser desfeita.` : ''}
        onConfirm={handleConfirmSelect}
        onCancel={() => !confirming && setTarget(null)}
      />

      {cancelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900">
            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Cancelar seleção</h2>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              "{cancelTarget.titulo}" volta a ficar Publicado e a emenda gerada ({money(cancelTarget.valorSolicitado)}) será
              apagada. Isso só é possível enquanto o período de cancelamento estiver aberto.
            </p>
            <label className="mt-4 block text-sm font-semibold text-fg" htmlFor="cancel-justificativa">
              Justificativa
            </label>
            <textarea
              id="cancel-justificativa"
              value={cancelJustificativa}
              onChange={(event) => setCancelJustificativa(event.target.value)}
              maxLength={500}
              rows={4}
              className={`${FIELD_CLASS} mt-1`}
              placeholder="Explique o motivo (mínimo de 10 caracteres)"
            />
            <div className="mt-4 flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setCancelTarget(null)} disabled={cancelando}>
                Voltar
              </Button>
              <Button variant="danger" onClick={() => void handleConfirmCancel()} disabled={cancelando || cancelJustificativa.trim().length < 10}>
                {cancelando ? 'Cancelando...' : 'Confirmar cancelamento'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestaoPreProjetosPage;
